import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';
import { 
    Calculator, Users, Wallet, CreditCard, ClipboardList, Package, TrendingUp,
    ArrowRight, Building2, LogOut, Settings, ChevronDown, Activity, BarChart3,
    AlertTriangle, Calendar, Briefcase, BookOpen
} from 'lucide-react';

const CalendarWidget = lazy(() => import('../components/CalendarWidget'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
    Calculator, Users, Wallet, CreditCard, ClipboardList, Package, TrendingUp, Building2, BookOpen
};

const moduleAccents = {
    blue: { gradient: 'from-blue-500 to-blue-600', glow: 'shadow-blue-500/20', iconBg: 'bg-blue-500/20', iconText: 'text-blue-300', border: 'border-blue-400/20' },
    purple: { gradient: 'from-violet-500 to-purple-600', glow: 'shadow-purple-500/20', iconBg: 'bg-purple-500/20', iconText: 'text-purple-300', border: 'border-purple-400/20' },
    emerald: { gradient: 'from-emerald-500 to-teal-600', glow: 'shadow-emerald-500/20', iconBg: 'bg-emerald-500/20', iconText: 'text-emerald-300', border: 'border-emerald-400/20' },
    amber: { gradient: 'from-amber-500 to-orange-600', glow: 'shadow-amber-500/20', iconBg: 'bg-amber-500/20', iconText: 'text-amber-300', border: 'border-amber-400/20' },
    cyan: { gradient: 'from-cyan-500 to-sky-600', glow: 'shadow-cyan-500/20', iconBg: 'bg-cyan-500/20', iconText: 'text-cyan-300', border: 'border-cyan-400/20' },
    teal: { gradient: 'from-teal-400 to-emerald-500', glow: 'shadow-teal-500/20', iconBg: 'bg-teal-500/20', iconText: 'text-teal-300', border: 'border-teal-400/20' }
};

const defaultModules = [
    { id: 'mod-contable', name: 'Sistema Contable', slug: 'contable', description: 'Gestión de transacciones, reportes financieros, bancos, activos y más', icon: 'Calculator', color: 'blue', order: 1 },
    { id: 'mod-ordenes', name: 'Órdenes de Trabajo', slug: 'ordenes', description: 'Gestión de órdenes de compra, reparaciones, reportes de actividad y más', icon: 'ClipboardList', color: 'cyan', order: 2 },
    { id: 'mod-inventario', name: 'Inventario', slug: 'inventario', description: 'Control de inventario, insumos, transferencias entre tiendas y reportes', icon: 'Package', color: 'blue', order: 3 },
    { id: 'mod-rrhh', name: 'Recursos Humanos', slug: 'rrhh', description: 'Gestión de empleados, planilla, contratos, ausencias y más', icon: 'Users', color: 'purple', order: 4 },
    { id: 'mod-caja', name: 'Flujo de Caja', slug: 'flujo-caja', description: 'Control de flujo de caja, ventas, gastos y libro de caja', icon: 'TrendingUp', color: 'emerald', order: 5 },
    { id: 'mod-pagos', name: 'Portal de Pagos', slug: 'pagos', description: 'Gestión de pagos, facturas, proveedores y boletas', icon: 'CreditCard', color: 'amber', order: 6 },
    { id: 'mod-biblioteca', name: 'Biblioteca de Herramientas', slug: 'biblioteca', description: 'Formatos, plantillas Excel, Word y recursos compartidos del equipo', icon: 'BookOpen', color: 'teal', order: 7 }
];

const BG_IMAGE = 'https://images.unsplash.com/photo-1770943558988-2c605d6bd5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwd29ya3NwYWNlJTIwYWVyaWFsfGVufDB8fHx8MTc3NDM4OTI2NXww&ixlib=rb-4.1.0&q=40&w=1920';

const Dashboard = () => {
    const { user, logout, isAdmin, token, storeInfo, canSeeAllStores } = useAuth();
    const navigate = useNavigate();
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [alertSummary, setAlertSummary] = useState({ total: 0, critical: 0, warning: 0 });

    useEffect(() => { fetchModules(); const t = setTimeout(fetchAlertSummary, 500); return () => clearTimeout(t); }, [user]);

    const fetchModules = () => {
        if (isAdmin || user?.role === 'Admin') {
            setModules(defaultModules);
        } else {
            setModules(defaultModules.filter(m => (user?.module_permissions || []).includes(m.slug)));
        }
        setLoading(false);
    };

    const fetchAlertSummary = async () => {
        try {
            const res = await axios.get(`${API}/alerts/summary`, { headers: { Authorization: `Bearer ${token}` } });
            let dismissed = new Set();
            try {
                const stored = localStorage.getItem('dismissed_alerts');
                if (stored) dismissed = new Set(JSON.parse(stored));
            } catch {}
            // If no dismissed alerts, use summary directly
            if (dismissed.size === 0) {
                setAlertSummary(res.data);
            } else {
                // Estimate: reduce counts proportionally (avoids full fetch)
                const total = Math.max(0, (res.data.total || 0) - dismissed.size);
                setAlertSummary({ ...res.data, total });
            }
        } catch { /* silent */ }
    };

    const userName = user?.full_name || user?.name || 'Usuario';
    const today = new Date().toLocaleDateString('es-GT', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const greeting = (() => {
        const h = new Date().getHours();
        if (h < 12) return 'Buenos días';
        if (h < 18) return 'Buenas tardes';
        return 'Buenas noches';
    })();

    return (
        <div className="min-h-screen relative" data-testid="dashboard-page">
            {/* Background Image */}
            <div 
                className="fixed inset-0 bg-cover bg-center bg-no-repeat" 
                style={{ backgroundImage: `url('${BG_IMAGE}')` }}
            />
            <div className="fixed inset-0 bg-gradient-to-br from-slate-900/85 via-blue-950/80 to-slate-900/90" />
            <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/10 via-transparent to-transparent" />

            {/* Content */}
            <div className="relative z-10">
                {/* Header */}
                <header className="border-b border-white/10 backdrop-blur-xl bg-white/5">
                    <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
                                <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-white tracking-wide">SIEMPRE</h1>
                                <p className="text-[11px] text-blue-200/70 -mt-0.5">Portal Empresarial</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <NotificationBell />
                            {isAdmin && (
                                <button onClick={() => navigate('/admin')} className="p-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition-all" data-testid="admin-link">
                                    <Settings className="w-5 h-5" />
                                </button>
                            )}
                            <div className="relative">
                                <button onClick={() => setShowUserMenu(!showUserMenu)} className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/10 transition-all" data-testid="user-menu-button">
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-sm font-semibold shadow-lg shadow-blue-500/20">
                                        {userName.charAt(0)}
                                    </div>
                                    <div className="hidden md:block text-left">
                                        <p className="text-sm font-medium text-white">{userName}</p>
                                        <p className="text-xs text-blue-200/60">{user?.role || 'Usuario'}</p>
                                    </div>
                                    <ChevronDown className="w-4 h-4 text-white/40" />
                                </button>
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-52 backdrop-blur-2xl bg-slate-800/90 border border-white/10 rounded-xl shadow-2xl py-2 z-50">
                                        <div className="px-4 py-2 border-b border-white/10 mb-1">
                                            <p className="text-sm font-medium text-white">{userName}</p>
                                            <p className="text-xs text-slate-400">{user?.email}</p>
                                        </div>
                                        <button onClick={() => { logout(); navigate('/login'); }} className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors" data-testid="logout-button">
                                            <LogOut className="w-4 h-4" />Cerrar Sesión
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="px-6 lg:px-10 py-8 max-w-7xl mx-auto">
                    {/* Welcome Section */}
                    <div className="mb-10">
                        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Calendar className="w-4 h-4 text-blue-300/80" />
                                    <p className="text-sm text-blue-200/70 capitalize">{today}</p>
                                </div>
                                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                                    {greeting}, <span className="bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">{userName}</span>
                                </h2>
                                <p className="text-base text-slate-300/80">
                                    {canSeeAllStores ? 'Acceso a todas las tiendas' : storeInfo?.assigned_store_name ? `Tienda: ${storeInfo.assigned_store_name}` : 'Selecciona un módulo para comenzar'}
                                </p>
                            </div>

                            {isAdmin && (
                                <div className="flex gap-3">
                                    <div className="flex items-center gap-3 px-5 py-3 backdrop-blur-xl bg-white/[0.07] border border-white/10 rounded-xl">
                                        <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                            <Activity className="w-4 h-4 text-emerald-400" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-400">Módulos</p>
                                            <p className="text-lg font-bold text-white">{modules.length}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 px-5 py-3 backdrop-blur-xl bg-white/[0.07] border border-white/10 rounded-xl">
                                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${alertSummary.critical > 0 ? 'bg-red-500/20' : alertSummary.warning > 0 ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
                                            <AlertTriangle className={`w-4 h-4 ${alertSummary.critical > 0 ? 'text-red-400' : alertSummary.warning > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-400">Alertas</p>
                                            <p className="text-lg font-bold text-white">{alertSummary.total}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 px-5 py-3 backdrop-blur-xl bg-white/[0.07] border border-white/10 rounded-xl">
                                        <div className="w-9 h-9 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                            <Briefcase className="w-4 h-4 text-blue-400" />
                                        </div>
                                        <div>
                                            <p className="text-xs text-slate-400">Rol</p>
                                            <p className="text-lg font-bold text-blue-300">{user?.role || 'Admin'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content: Modules + Calendar */}
                    <div className="flex gap-6 flex-col xl:flex-row">
                        {/* Module Grid */}
                        <div className="flex-1">
                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {[1, 2, 3].map((i) => <div key={i} className="h-52 bg-white/5 rounded-2xl animate-pulse border border-white/5" />)}
                                </div>
                            ) : modules.length === 0 ? (
                                <div className="text-center py-20 backdrop-blur-xl bg-white/[0.05] rounded-2xl border border-white/10">
                                    <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-4">
                                        <Building2 className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-white mb-2">Sin módulos asignados</h3>
                                    <p className="text-slate-400">Contacta al administrador para obtener acceso.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-5">
                                    {modules.map((module, index) => {
                                        const IconComponent = iconMap[module.icon] || Building2;
                                        const accent = moduleAccents[module.color] || moduleAccents.blue;
                                        return (
                                            <button
                                                key={module.id}
                                                onClick={() => navigate(`/modulo/${module.slug}`)}
                                                className={`group relative overflow-hidden rounded-2xl backdrop-blur-xl bg-white/[0.07] border border-white/[0.08] hover:bg-white/[0.12] hover:border-white/[0.15] transition-all duration-500 text-left shadow-lg ${accent.glow} hover:shadow-xl hover:scale-[1.02]`}
                                                data-testid={`module-card-${module.slug}`}
                                                style={{ animationDelay: `${index * 80}ms` }}
                                            >
                                                <div className={`h-1 bg-gradient-to-r ${accent.gradient} opacity-60 group-hover:opacity-100 transition-opacity`} />
                                                <div className="p-6">
                                                    <div className="flex items-start justify-between mb-5">
                                                        <div className={`w-14 h-14 rounded-2xl ${accent.iconBg} flex items-center justify-center transition-transform group-hover:scale-110`}>
                                                            <IconComponent className={`w-7 h-7 ${accent.iconText}`} />
                                                        </div>
                                                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-all">
                                                            <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-white/70 group-hover:translate-x-0.5 transition-all" />
                                                        </div>
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-100 transition-colors">{module.name}</h3>
                                                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">{module.description}</p>
                                                </div>
                                                <div className={`absolute -bottom-20 -right-20 w-40 h-40 bg-gradient-to-br ${accent.gradient} rounded-full opacity-0 group-hover:opacity-[0.06] blur-3xl transition-opacity duration-500`} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Calendar Widget */}
                        <div className="xl:w-80 2xl:w-96 flex-shrink-0">
                            <Suspense fallback={<div className="backdrop-blur-xl bg-white/[0.07] border border-white/[0.08] rounded-2xl h-96 animate-pulse" />}>
                                <CalendarWidget />
                            </Suspense>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-12 text-center">
                        <p className="text-sm text-slate-500/60">SIEMPRE - Portal Empresarial &copy; {new Date().getFullYear()}</p>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Dashboard;
